import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  AxiosCacheInstance,
  CacheRequestConfig,
  CacheUpdaterRecord,
  MemoryStorage,
  buildKeyGenerator,
  setupCache,
} from 'axios-cache-interceptor';
import { configuration } from '../configuration';
import { ApiList } from './api/ApiList';

declare module 'axios' {
  export interface AxiosRequestConfig<D = any> {
    /**
     * Skips the global "something went wrong on our end" modal for this one
     * request, so the caller can surface the server's own message instead
     * (APP-2254's Entity Details lock returns a 403 written for the member).
     * On a GET it leaves the global error page and `currentError` untouched,
     * which a CSV export's retried and cancelled page requests rely on
     * (APP-2436).
     *
     * Per request rather than per URL: the legacy forms PATCH the same
     * endpoints through the Redux thunks and never render `errorResponse`, so
     * a URL rule would leave them with no feedback at all. Only set this where
     * the caller definitely shows the error itself.
     */
    suppressErrorModal?: boolean;
  }
}

const apis = [
  ...(configuration.COGNITO_ENABLED ? [] : [configuration.AUTH0_DOMAIN_URL]),
  ...Object.values(ApiList),
];

// Create an instance of Axios
const axiosInstance = axios.create();

// https://axios-cache-interceptor.js.org/guide/getting-started
setupCache(axiosInstance, {
  generateKey: buildKeyGenerator((request) =>
    // Remove the base URL from the cache key
    request.url?.replace(/^https?:\/\/[\w.]+(:[\d]{4})?\//, ''),
  ),
});

// Add a request interceptor for applying Authorization header
axiosInstance.interceptors.request.use(
  async (config) => {
    const { reduxServices } = await import('../reducers/reduxServices');

    // Only add our Authorization header / access token to requests to our own APIs
    const baseUrl = config.baseURL || config.url;

    if (apis.some((base) => base && baseUrl?.includes(base))) {
      const accessToken =
        await reduxServices.globalState.getAccessTokenSilently();

      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Add a response interceptor for handling errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const { reduxServices } = await import('../reducers/reduxServices');

    const status = error.response?.status;
    const url = error.config?.url;

    // Special handling added for /airwallex/auth-code endpoint to avoid a full-page 'access denied' message
    if (status === 401 && !url?.includes('/airwallex/auth-code')) {
      // Dispatch actions to reset app state
      await reduxServices.resetAppState();
    } else {
      const method = error.config?.method?.toUpperCase();
      const baseURL = window.location.pathname;

      // A suppressed GET reports its own failure (a CSV export's toast), so it
      // must not touch global error state: setting `currentError`, or calling
      // `showErrorPage(false)`, would clear an error page another request has
      // just raised. Cancel's aborts land here too.
      if (method === 'GET' && error.config?.suppressErrorModal) {
        throw error;
      }

      // Set current error in state
      reduxServices.setCurrentError({
        code: error?.code,
        message: error?.message,
        method: method,
        status: status,
      });

      // In general, show modal for non-GET requests,
      // Lookups, or download requests
      if (
        method !== 'GET' ||
        url?.includes('/business-number-lookup') ||
        url?.includes('/download') ||
        url?.includes('/invitation/check-existing') ||
        url?.includes(configuration.DOCUMENTS_S3_BUCKET)
      ) {
        let showErrorModal = true;

        // Capability to ignore errors for specific path/status
        if (
          // 400 responses are handed by the forms
          (url?.includes('/business-number-lookup') && status === 400) ||
          (url?.includes('/invitation') && status === 400) ||
          (url?.includes('/payment-rule') && status === 400)
        ) {
          showErrorModal = false;
        }

        // Todo: Remove this once we have a proper error handling for this endpoint
        // Ignore 404 for invitation/check-existing
        if (url?.includes('/invitation/check-existing') && status === 403) {
          showErrorModal = false;
        }

        // Opted out by the caller because it renders the server's message
        // itself — see `suppressErrorModal` above (APP-2254).
        if (error.config?.suppressErrorModal) {
          showErrorModal = false;
        }

        reduxServices.showErrorModal(showErrorModal);
      } else {
        let showErrorPage = true;

        // Capability to ignore errors for specific path/status
        if (url?.includes('/business-number-lookup')) {
          showErrorPage = false;
        }

        // Capability to ignore errors for specific path/status
        if (
          // 404 is expected for users/me at first login
          url?.includes('/v2/user/me') &&
          status === 404
        ) {
          showErrorPage = false;
        }

        // Ignore cannot find client in title summary and staff title summary
        if (
          url?.includes('/client') &&
          status === 404 &&
          (baseURL?.includes('/staff/workspaces/manage-workspaces') ||
            baseURL?.includes('/titles/summary'))
        ) {
          showErrorPage = false;
        }
        reduxServices.showErrorPage(showErrorPage);
      }

      throw error;
    }
  },
);

export const axiosCacheInstance = axiosInstance as AxiosCacheInstance;

export const axiosCacheStorage = axiosCacheInstance.storage as MemoryStorage;

const getMatchingCacheKeys = (partialKeys: string[]) => {
  // `axiosCacheStorage.data` can be a Map or a plain object depending on the version and configuration
  const cacheMap = axiosCacheStorage.data;
  const cacheKeys =
    cacheMap instanceof Map
      ? Array.from(cacheMap.keys())
      : Object.keys(cacheMap);

  return cacheKeys.filter((cachedKey) =>
    partialKeys.some((key) => cachedKey.includes(key)),
  );
};

export const invalidateCacheRequestConfig = (...partialKeys: string[]) => {
  const cacheUpdaterValue = getMatchingCacheKeys(partialKeys).reduce(
    (map: CacheUpdaterRecord<any, any>, key) => {
      map[key] = 'delete';

      return map;
    },
    {},
  );

  const config: CacheRequestConfig = {
    cache: {
      update: cacheUpdaterValue,
    },
  };

  return config;
};

/**
 * Skips the axios cache for one request. React Query reads pass it: React Query
 * is their only cache, so `invalidateQueries` refetches from the server instead
 * of getting the axios cache's copy for up to 5 minutes.
 */
export const bypassCacheConfig = (): CacheRequestConfig => {
  return {
    cache: false,
  };
};

export default axiosInstance;
